import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { facilityAPI } from '../api';
import { toast } from 'sonner';
import { toastApiError } from '../lib/utils';
import { Plus, Search, MapPin, Mail, Phone, Globe, Clock } from 'lucide-react';

const Facilities = () => {
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [newFacility, setNewFacility] = useState({
    name: '', address: '', city: '', state: '', zip_code: '',
    contact_phone: '', contact_email: '', receiving_hours: '',
    scheduling_method: 'Email', scheduling_email: '', scheduling_phone: '',
    portal_url: '', portal_instructions: '', notes: ''
  });

  useEffect(() => {
    fetchFacilities();
  }, []);

  useEffect(() => {
    const filtered = facilities.filter(f => 
      f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.city.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredFacilities(filtered);
  }, [searchTerm, facilities]);

  const fetchFacilities = async () => {
    try {
      const response = await facilityAPI.getAll();
      setFacilities(response.data);
      setFilteredFacilities(response.data);
    } catch (error) {
      toast.error('Failed to load facilities');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFacility = async (e) => {
    e.preventDefault();
    try {
      await facilityAPI.create(newFacility);
      toast.success('Facility created successfully');
      setShowDialog(false);
      fetchFacilities();
      setNewFacility({ name: '', address: '', city: '', state: '', zip_code: '', contact_phone: '', contact_email: '', receiving_hours: '', scheduling_method: 'Email', scheduling_email: '', scheduling_phone: '', portal_url: '', portal_instructions: '', notes: '' });
    } catch (error) {
      toastApiError(toast, error, 'Facility could not be created');
    }
  };

  return (
    <MainLayout>
      <div className="" data-testid="facilities-page">
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--tms-text)]">Facilities</h1>
            <p className="text-sm md:text-base text-[var(--tms-text-secondary)] mt-1">Manage pickup and delivery locations</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-facility-button" size="sm" className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" /> Add Facility
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add New Facility</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateFacility} className="space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name">Facility Name *</Label>
                    <Input id="name" value={newFacility.name} onChange={(e) => setNewFacility({...newFacility, name: e.target.value})} required data-testid="input-name" data-field="name" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input id="address" value={newFacility.address} onChange={(e) => setNewFacility({...newFacility, address: e.target.value})} required data-testid="input-address" data-field="address" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input id="city" value={newFacility.city} onChange={(e) => setNewFacility({...newFacility, city: e.target.value})} required data-testid="input-city" data-field="city" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input id="state" value={newFacility.state} onChange={(e) => setNewFacility({...newFacility, state: e.target.value})} required data-testid="input-state" data-field="state" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP Code *</Label>
                    <Input id="zip" value={newFacility.zip_code} onChange={(e) => setNewFacility({...newFacility, zip_code: e.target.value})} required data-testid="input-zip" data-field="zip_code" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={newFacility.contact_phone} onChange={(e) => setNewFacility({...newFacility, contact_phone: e.target.value})} placeholder="Optional" data-testid="input-phone" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={newFacility.contact_email} onChange={(e) => setNewFacility({...newFacility, contact_email: e.target.value})} placeholder="Optional" data-testid="input-email" />
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="submit" data-testid="submit-facility-button">Create Facility</Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>


        <div className="stats-card">
          {loading ? (
            <div className="text-center py-12 text-[var(--tms-text-secondary)]">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr><th>Facility Name</th><th>Location</th><th>Receiving Hours</th><th>Scheduling Method</th></tr>
                </thead>
                <tbody>
                  {filteredFacilities.length === 0 ? (
                    <tr><td colSpan="4" className="text-center py-12 text-[var(--tms-text-muted)]">{searchTerm ? 'No facilities found' : 'No facilities yet. Add your first facility to get started.'}</td></tr>
                  ) : (
                    filteredFacilities.map((facility) => {
                      const getSchedulingAction = () => {
                        switch (facility.scheduling_method) {
                          case 'Email':
                            return facility.scheduling_email 
                              ? `mailto:${facility.scheduling_email}?subject=Appointment Request - ${facility.name}`
                              : null;
                          case 'Phone':
                            return facility.scheduling_phone 
                              ? `tel:${facility.scheduling_phone}`
                              : null;
                          case 'Portal':
                            return facility.portal_url || null;
                          default:
                            return null;
                        }
                      };

                      const schedulingAction = getSchedulingAction();
                      const isClickable = schedulingAction && facility.scheduling_method !== 'FCFS';

                      return (
                        <tr key={facility.id} data-testid={`facility-row-${facility.id}`}>
                          <td>
                            <Link to={`/facilities/${facility.id}`} className="font-semibold text-blue-600 hover:text-blue-700" data-testid={`facility-link-${facility.id}`}>
                              {facility.name}
                            </Link>
                          </td>
                          <td><div className="flex items-center gap-1 text-sm text-[var(--tms-text-secondary)]"><MapPin className="w-3 h-3" />{facility.city}, {facility.state} {facility.zip_code}</div></td>
                          <td className="text-sm">{facility.receiving_hours}</td>
                          <td>
                            {isClickable ? (
                              <a
                                href={schedulingAction}
                                target={facility.scheduling_method === 'Portal' ? '_blank' : undefined}
                                rel={facility.scheduling_method === 'Portal' ? 'noopener noreferrer' : undefined}
                                className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors cursor-pointer"
                                data-testid={`scheduling-link-${facility.id}`}
                                title={
                                  facility.scheduling_method === 'Email' ? `Email: ${facility.scheduling_email}` :
                                  facility.scheduling_method === 'Phone' ? `Call: ${facility.scheduling_phone}` :
                                  facility.scheduling_method === 'Portal' ? `Portal: ${facility.portal_url}` : ''
                                }
                              >
                                {facility.scheduling_method === 'Email' && <Mail className="w-3 h-3" />}
                                {facility.scheduling_method === 'Phone' && <Phone className="w-3 h-3" />}
                                {facility.scheduling_method === 'Portal' && <Globe className="w-3 h-3" />}
                                {facility.scheduling_method}
                              </a>
                            ) : (
                              <span 
                                className="inline-flex items-center gap-1 text-xs bg-[var(--tms-hover)] text-[var(--tms-text-secondary)] px-2 py-1 rounded"
                                data-testid={`scheduling-badge-${facility.id}`}
                                title={facility.scheduling_method === 'FCFS' ? 'First Come First Served - No appointment needed' : 'Scheduling method not configured'}
                              >
                                {facility.scheduling_method === 'FCFS' && <Clock className="w-3 h-3" />}
                                {facility.scheduling_method}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Facilities;